import { Button, Divider, Drawer } from '@prio365/prio365-react-library';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DriveItem } from '../../../../models/Drive';
import { DriveItemId, ProjectId } from '../../../../models/Types';
import {
  Col,
  Form,
  Input,
  InputProps,
  notification,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Typography,
} from 'antd';
import Flex from '../../../../components/Flex';
import DriveItemBreadcrumb from '../DriveItemBreadcrumb/DriveItemBreadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentFolderItem,
  getCurrentRemoteItem,
  getProject,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import { Project } from '../../../../models/Project';
import { checkFileNameExists, fileNameRegex } from '../../util';
import {
  abortDriveItemFetch,
  driveItemCreated,
  DriveItemsFetchContext,
  fetchDriveItemsSagaAction,
} from '../../actions';
import FolderSelectionDrawer from './FolderSelectionDrawer';
import { NewDocumentType } from '../../../../models/Document';
import { apiCreateNewDocument } from '../../api';
import i18n from '../../../../i18n';
import DocumentSuccessNotification from '../Notifications/DocumentSuccessNotification';
import { debounceFunction } from '../../../../util';

const debouncedInputChange = debounceFunction(
  (value: string, onChange?: (value: string) => void) => {
    if (onChange) {
      onChange(value);
    }
  },
  150
);

interface NewDocumenDrawerProps {
  visible?: boolean;
  driveItem: DriveItem;
  projectId: string;
  groupId: string;
  parentFolderDriveItem: DriveItem;
  onCancel?: () => void;
  onSuccess?: (driveItem?: DriveItem, projectId?: ProjectId) => void;
  documentType?: NewDocumentType;
  prefix?: string;
}

export const NewDocumentDrawer: React.FC<NewDocumenDrawerProps> = (props) => {
  const {
    visible = false,
    onCancel: _onCancel = () => {},
    projectId,
    groupId,
    parentFolderDriveItem,
    documentType,
    prefix,
  } = props;
  //#region ------------------------------------------------- Defaults
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [isFormValid, setIsFormValid] = useState(false);
  const [checkingName, setCheckingName] = useState<boolean>(false);
  const [nameExists, setNameExists] = useState<boolean>(false);
  const [nameIsValid, setNameIsValid] = useState<boolean>(true);
  const [documentName, setDocumentName] = useState<string>('');
  const [selectTargetFolderDrawerVisible, setSelectTargetFolderDrawerVisible] =
    useState<boolean>(false);
  const [selectedProjectId, setSelectedProjectId] =
    useState<ProjectId>(projectId);
  const [saveFolderChoice, setSaveFolderChoice] = useState<
    'customFolder' | string
  >('customFolder');
  const [targetFolderId, setTargetFolderId] = useState<DriveItemId>(
    parentFolderDriveItem?.id
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const targetFolder = useSelector<RootReducerState, DriveItem>((state) =>
    getCurrentFolderItem(state, targetFolderId)
  );
  const [saveFolderDriveItemId, setSaveFolderDriveItemId] =
    useState<DriveItemId>(targetFolderId);
  const project = useSelector<RootReducerState, Project>((state) =>
    getProject(state, selectedProjectId)
  );
  const currentRemoteItem = useSelector(getCurrentRemoteItem);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const onCancel = () => {
    _onCancel();
    dispatch(
      abortDriveItemFetch(DriveItemsFetchContext.Other, undefined, true)
    );
  };

  function handleClose() {
    setDocumentName('');
    setNameExists(false);
    setNameIsValid(false);
    form.resetFields();
    onCancel();
  }
  const handleSaveFolderChoice = (e: RadioChangeEvent) => {
    setSaveFolderChoice(e.target.value);
    if (e.target.value !== 'customFolder') {
      setSaveFolderDriveItemId(e.target.value);
    } else {
      setTargetFolderId(targetFolder !== null ? targetFolder?.id : null);
      setSaveFolderDriveItemId(targetFolder !== null ? targetFolder?.id : null);
    }
  };

  const openTargetFolderDrawer = () => {
    setSelectTargetFolderDrawerVisible(true);
  };

  const handleOnBreadcrumbItemClick = (driveItemId: DriveItemId) => {
    setTargetFolderId(driveItemId);
    setSaveFolderDriveItemId(driveItemId);
    if (!!project) {
      const { groupId, projectId } = project;
      dispatch(
        fetchDriveItemsSagaAction(
          projectId,
          groupId,
          driveItemId,
          driveItemId === null,
          DriveItemsFetchContext.Other
        )
      );
    }
  };

  const handleFormValuesChange = async () => {
    if (setIsFormValid) {
      try {
        await form.validateFields();
        setIsFormValid(true);
      } catch (error) {
        setIsFormValid(false);
      }
    }
  };

  const handleTargetFolderDrawerOk = (targetFolder: DriveItem | null) => {
    setSelectTargetFolderDrawerVisible(false);
    setTargetFolderId(targetFolder?.id);
    setSaveFolderDriveItemId(targetFolder?.id);
  };
  const handleTargetFolderDrawerCancel = () => {
    setSelectTargetFolderDrawerVisible(false);
  };

  const handleOnFinish = async () => {
    setIsSaving(true);
    try {
      const { data } = await apiCreateNewDocument({
        documentName: prefix ? prefix + documentName : documentName,
        documentType: documentType,
        saveFolderDriveItemId: saveFolderDriveItemId,
        groupId,
        projectId: selectedProjectId,
      });

      if (data) {
        const key = `open${Date.now()}`;
        notification.open({
          message: i18n.t('common:documents:success'),
          description: (
            <DocumentSuccessNotification
              driveItem={data}
              notificationKey={key}
              project={project}
            />
          ),
          key,
          duration: 10,
        });
        dispatch(driveItemCreated(data, saveFolderDriveItemId, groupId));
        setIsSaving(false);
        handleClose();
      } else {
        notification.open({
          message: t('common:error'),
          description: t(
            'documents:newDocumentForm.errorMessages.createDocumentError'
          ),
        });

        setIsSaving(false);
      }
    } catch (error) {
      setIsSaving(false);
    }
  };

  const handleChange = async (value: string) => {
    setDocumentName(value);
    const isValid = !!value.match(fileNameRegex) && value.trim().length > 0;
    setNameIsValid(isValid);
    const fullName = `${prefix ?? ''}${value.trim()}${documentType ? `.${documentType}` : ''}`;

    if (isValid) {
      setCheckingName(true);
      const fileExists = await checkFileNameExists(
        groupId,
        targetFolder?.id,
        fullName
      );
      setNameExists(fileExists);
      setCheckingName(false);
    } else {
      setNameExists(false);
    }
  };

  const getDrawerTitle = () => {
    switch (documentType) {
      case 'docx':
        return t('documents:navigationBar.createWordDocument');
      case 'xlsx':
        return t('documents:navigationBar.createExcelDocument');
      case 'pptx':
        return t('documents:navigationBar.createPowerPointDocument');
      default:
        return t('documents:navigationBar.createDocument');
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (parentFolderDriveItem?.id) {
      setTargetFolderId(parentFolderDriveItem.id);
      setSaveFolderDriveItemId(parentFolderDriveItem.id);
    }
  }, [parentFolderDriveItem]);

  useEffect(() => {
    form
      .validateFields(['documentName'])
      .then(() => {
        setIsFormValid(true);
      })
      .catch(() => {
        setIsFormValid(false);
      });
  }, [nameExists, form]);
  //#endregion

  return (
    <Drawer
      zIndex={1000}
      title={getDrawerTitle()}
      visible={visible}
      onClose={handleClose}
      customWidth={800}
      closable={true}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button type="default" onClick={handleClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={() => handleOnFinish()}
            disabled={
              !isFormValid ||
              !nameIsValid ||
              isSaving ||
              nameExists ||
              !documentName.trim()
            }
            loading={isSaving || checkingName}
            style={{
              marginLeft: '12px',
            }}
          >
            {t('common:actions.save')}
          </Button>
        </div>
      }
    >
      {selectedProjectId && (
        <>
          <Typography.Title level={5}>
            {t('documents:documentTemplates.generalHeadline')}
          </Typography.Title>
          <Form
            form={form}
            onFinish={handleOnFinish}
            onValuesChange={handleFormValuesChange}
            layout="vertical"
            initialValues={{
              documentName: '',
              targetFolder: targetFolderId,
            }}
            id={'prio-project-new-documents-form'}
          >
            <Flex.Row>
              <Flex.Item flex={1}>
                <div
                  style={{
                    flex: 1,
                  }}
                >
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="documentName"
                        validateTrigger="onBlur"
                        label={t(
                          'documents:newDocumentForm.labels.documentName'
                        )}
                        style={{ marginBottom: '8px' }}
                        rules={[
                          {
                            required: true,
                            validator: async (_, value) => {
                              if (!value) {
                                return Promise.reject(
                                  t(
                                    'documents:newDocumentForm.validation.missingDocumentName'
                                  )
                                );
                              }

                              if (!nameIsValid) {
                                return Promise.reject(
                                  t(
                                    'documents:confirmation.rename.invalidFileName'
                                  )
                                );
                              }

                              if (nameExists) {
                                return Promise.reject(
                                  t('documents:confirmation.rename.nameExists')
                                );
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <DebouncedInput
                          prefix={`${prefix ? prefix + '-' : ''}`}
                          disabled={isSaving}
                          value={documentName}
                          onChange={handleChange}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Divider style={{ marginTop: '8px' }} />
                  <Typography.Title level={5}>
                    {t('documents:newDocumentForm.labels.saveFolderChoice')}
                  </Typography.Title>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        label={
                          <div>
                            {t(`documents:newDocumentForm.labels.targetFolder`)}
                            <Button
                              onClick={openTargetFolderDrawer}
                              iconProp={['fal', 'pen']}
                              type="link"
                            ></Button>
                          </div>
                        }
                      >
                        {
                          <Radio.Group
                            value={saveFolderChoice}
                            onChange={handleSaveFolderChoice}
                            disabled={isSaving}
                          >
                            <Space direction="vertical">
                              <Radio value="customFolder" key="customFolder">
                                <DriveItemBreadcrumb
                                  driveItem={
                                    targetFolder ?? parentFolderDriveItem
                                  }
                                  remoteItem={currentRemoteItem}
                                  project={project}
                                  onItemClick={handleOnBreadcrumbItemClick}
                                  disableLinkNavigation
                                />
                              </Radio>
                            </Space>
                          </Radio.Group>
                        }
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Flex.Item>
            </Flex.Row>
          </Form>
        </>
      )}
      {project && (
        <FolderSelectionDrawer
          visible={selectTargetFolderDrawerVisible}
          onOk={handleTargetFolderDrawerOk}
          onCancel={handleTargetFolderDrawerCancel}
          projectId={selectedProjectId}
          selectedFolder={targetFolder?.id ?? parentFolderDriveItem?.id}
          allowProjectChange
          setProjectId={setSelectedProjectId}
        />
      )}
    </Drawer>
  );
};

type DebouncedInputProps = Omit<InputProps, 'onChange'> & {
  onChange?: (value: string) => void;
};

const DebouncedInput: React.FC<DebouncedInputProps> = (props) => {
  //#region ------------------------------ Defaults
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [innerValue, setInnerValue] = useState<string>('');
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    setInnerValue(newValue);
    debouncedInputChange(newValue, props.onChange);
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    setInnerValue((props.value as string) ?? '');
  }, [props.value]);
  //#endregion

  return <Input {...props} value={innerValue} onChange={handleOnChange} />;
};
